import { Button } from "@/components/ui/button";
import { WorkflowHeader } from "@/components/ui/WorkflowHeader";

interface TemplateStudioWorkflowHeaderBaseProps {
  hasCanvasContent: boolean;
  isWorking: boolean;
  isSaving?: boolean;
  onCreateTemplate: () => void;
  onClose: () => void;
}

type TemplateStudioWorkflowHeaderModeProps =
  | { mode?: "create"; onClearCanvas: () => void }
  | {
      /** Editing a saved template: Discard changes replaces Clear canvas. */
      mode: "edit";
      hasUnsavedChanges: boolean;
      onDiscardChanges: () => void;
    };

export type TemplateStudioWorkflowHeaderProps =
  TemplateStudioWorkflowHeaderBaseProps & TemplateStudioWorkflowHeaderModeProps;

const secondaryActionClassName = "h-7 min-w-0 px-2.5 text-xs font-medium";

export const TemplateStudioWorkflowHeader = ({
  hasCanvasContent,
  isWorking,
  isSaving = false,
  onCreateTemplate,
  onClose,
  ...modeProps
}: TemplateStudioWorkflowHeaderProps) => {
  const isEditing = modeProps.mode === "edit";
  const isBusy = isWorking || isSaving;
  const canSave =
    hasCanvasContent && !isBusy && (!isEditing || modeProps.hasUnsavedChanges);
  const saveLabel = isEditing ? "Save changes" : "Create template";

  return (
    <WorkflowHeader
      title={isEditing ? "Edit template" : "New template"}
      onClose={onClose}
      actions={
        <>
          {modeProps.mode === "edit" ? (
            <Button
              variant="outline"
              size="sm"
              onPress={modeProps.onDiscardChanges}
              isDisabled={!modeProps.hasUnsavedChanges || isBusy}
              className={secondaryActionClassName}
            >
              Discard changes
            </Button>
          ) : hasCanvasContent ? (
            <Button
              variant="outline"
              size="sm"
              onPress={modeProps.onClearCanvas}
              isDisabled={isBusy}
              className={secondaryActionClassName}
            >
              Clear canvas
            </Button>
          ) : null}
          <Button
            variant="default"
            size="sm"
            onPress={onCreateTemplate}
            isDisabled={!canSave}
            className="h-7 min-w-0 px-2.5 text-xs font-medium"
          >
            {isSaving ? (isEditing ? "Saving…" : "Creating…") : saveLabel}
          </Button>
        </>
      }
    />
  );
};
